import { useState, useEffect } from 'react';
import { API_BASE } from '@/src/infrastructure/lib/api';

type ExperienceStatus = 'PENDING' | 'VALIDATING' | 'GENERATING' | 'UPLOADING' | 'COMPILING' | 'COMPLETE' | 'FAILED';
type ExperienceStep = 'validate' | 'prompts' | 'images' | 'compile' | 'finalize';

interface ExperienceStatusResponse {
  status: ExperienceStatus;
  step: ExperienceStep;
}

export function useExperienceStatus(invitationSlug: string) {
  const [data, setData] = useState<ExperienceStatusResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!invitationSlug) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    let isMounted = true;

    const fetchStatus = async () => {
      try {
        const res = await fetch(`${API_BASE}/public/${invitationSlug}/experience-status`);
        if (!res.ok) {
          throw new Error(`Failed to fetch status: ${res.statusText}`);
        }
        const json = await res.json();
        
        if (isMounted) {
          setData(json);
          setError(null);

          if (json.status !== 'COMPLETE' && json.status !== 'FAILED') {
            timeoutId = setTimeout(fetchStatus, 5000);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Unknown error'));
          // Retry on error
          timeoutId = setTimeout(fetchStatus, 5000);
        }
      }
    };

    fetchStatus();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [invitationSlug]);

  return { data, error };
}
